/**
 * 精選藝人排序 and 專頁同步 helpers. Pure — no React / Firebase — so node:test can import them.
 */

export interface FeaturedArtist {
  artistId: string;
  displayName: string;
  avatarUrl: string | null;
  avatarPath: string;
  followerCount: number;
  featuredOrder: number;
  pinned: boolean;
}
export interface FeaturedPage {
  items: FeaturedArtist[];
  /** How many artists the App shows (functions SUGGESTED_ARTISTS). */
  visibleCount: number;
  maxPinned: number;
}

/** Pinned artistIds in their saved order. */
export const pinnedIds = (items: FeaturedArtist[]): string[] =>
  items.filter((i) => i.pinned).sort((a, b) => a.featuredOrder - b.featuredOrder).map((i) => i.artistId);

/**
 * The order the App will show after saving `pinned`: pinned artists first in the given order, then everyone else by
 * followers desc and document ID desc (same tie-break as the backend query).
 */
export function featuredPreview(items: FeaturedArtist[], pinned: string[]): (FeaturedArtist & { pinnedRank: number | null })[] {
  const byId = new Map(items.map((i) => [i.artistId, i]));
  const head = pinned.flatMap((id, n) => (byId.has(id) ? [{ ...byId.get(id)!, pinnedRank: n + 1 }] : []));
  const set = new Set(pinned);
  const tail = items
    .filter((i) => !set.has(i.artistId))
    .sort((a, b) => b.followerCount - a.followerCount || (a.artistId < b.artistId ? 1 : -1))
    .map((i) => ({ ...i, pinnedRank: null }));
  return [...head, ...tail];
}

/** Moves the pinned artist at `index` by `delta` (−1 up, +1 down); out-of-range moves return the same list. */
export function movePinned(pinned: string[], index: number, delta: number): string[] {
  const to = index + delta;
  if (index < 0 || index >= pinned.length || to < 0 || to >= pinned.length) return pinned;
  const next = [...pinned];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

export const sameOrder = (a: string[], b: string[]): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

/* ---------- 同步已儲存資料（publishProfile）覆蓋提示 ---------- */

export interface SyncOverwrite {
  field: "displayName" | "bio";
  label: string;
  current: string;
  next: string;
}

/**
 * Which App profile fields publishProfile would replace: it copies actors.name (≤60) and actors.biography (≤1000),
 * trimmed, over artistProfiles.displayName / bio — including anything the artist edited in the App.
 */
export function syncOverwrites(
  actor: { name?: string | null; biography?: string | null },
  profile: { displayName?: string | null; bio?: string | null },
): SyncOverwrite[] {
  // Same as the backend text(): trim, then String.slice.
  const clip = (v: string | null | undefined, max: number) => (v ?? "").trim().slice(0, max);
  const rows: SyncOverwrite[] = [
    { field: "displayName", label: "名稱", current: clip(profile.displayName, 60), next: clip(actor.name, 60) },
    { field: "bio", label: "簡介", current: clip(profile.bio, 1000), next: clip(actor.biography, 1000) },
  ];
  return rows.filter((r) => r.current !== r.next);
}

export function featuredErrorMessage(code: string | undefined, status: number): string {
  if (status === 401 || code === "UNAUTHENTICATED") return "請重新登入後操作";
  if (status === 403 || code === "FORBIDDEN") return "需要管理員或工作人員權限";
  if (code === "NOT_FOUND") return "有藝人已下架或不存在，請重新整理後再排序";
  if (status === 404) return "後端尚未部署精選排序功能（artistHub）";
  if (code === "INVALID_ARGUMENT") return "排序資料不正確，請重新整理後再試";
  return "操作未完成，請重試";
}
